/**
 * Canonical surface / control class strings for mode panels (palette-pass Slice 0).
 *
 * Light + dark pairs are locked here so panel chrome stays consistent across
 * buckets. `SectionCard` and shell wrappers import from this module; feature
 * lanes should prefer these over inlining `bg-white` + ad-hoc `dark:` pairs.
 *
 * Dark neutrals use Tailwind `surface-*` colors backed by CSS variables
 * (`src/surfaceTokens.css`): zinc defaults on `.dark`, warm charcoal on
 * `.dark.special` (special dark mode Slice 3). Full §4.2 token migration
 * still lands later in `tailwind.config` theme blocks.
 */

/** Page canvas behind mode panels (workspace `<main>`). */
export const workspaceMainClass =
  "flex-1 min-w-0 overflow-y-auto flex flex-col bg-gray-100 dark:bg-surface-canvas";

/** Elevated card shell — border, fill, shadow. No padding. */
export const cardSurfaceClass =
  "rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-surface-border dark:bg-surface-canvas";

/** Default `SectionCard` chrome (doctrine §K2). */
export const sectionCardClass = `${cardSurfaceClass} p-4 space-y-3`;

/**
 * Mobile workspace phone-card (`LeadWorkspaceShell`, below `lg`). Desktop
 * resets to a transparent, edge-to-edge wrapper.
 */
export const shellMobileCardClass =
  "w-full max-w-3xl mx-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8 text-center " +
  "dark:bg-surface-canvas dark:shadow-black/40 " +
  "lg:max-w-none lg:bg-transparent lg:rounded-none lg:shadow-none lg:p-0 lg:text-left " +
  "lg:dark:bg-transparent lg:dark:shadow-none";

/** Full-width text field / select on a card surface. */
export const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none " +
  "focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Neutral outline button on a card surface. */
export const ghostBtnClass =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 " +
  "disabled:cursor-not-allowed disabled:opacity-60 " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

/** Secondary / outline control — lighter border than `ghostBtnClass`. */
export const secondaryBtnClass =
  "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 " +
  "disabled:cursor-not-allowed disabled:opacity-60 " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

export const cardTitleTextClass = "text-gray-900 dark:text-surface-ink";
export const cardMutedTextClass = "text-gray-500 dark:text-surface-ink-muted";
export const cardDividerBorderClass = "border-gray-100 dark:border-surface-border";
export const cardHoverSurfaceClass = "hover:bg-gray-50 dark:hover:bg-surface-hover";

/** Inset card on neutral or tinted panel backgrounds. */
export const insetSurfaceCardClass =
  "rounded-xl border border-gray-200 bg-white p-3 dark:border-surface-border dark:bg-surface-canvas";

/** Secondary / recessed panel shell (progress blocks, search areas). */
export const secondarySurfaceClass =
  "rounded-2xl border border-gray-200 bg-gray-50 dark:border-surface-border dark:bg-surface-canvas";

/** Compact field on a card surface. */
export const compactInputClass =
  "rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

/** Sales Console indigo grouping plate. */
export const salesIndigoPanelClass =
  "rounded-xl border border-indigo-200 bg-indigo-50 p-3 shadow-inner " +
  "dark:border-indigo-800/70 dark:bg-indigo-950/35";

/** White/indigo inset card inside Sales Console sections. */
export const salesIndigoSurfaceCardClass =
  "rounded-xl border border-indigo-100 bg-white p-3 dark:border-indigo-800/60 dark:bg-surface-canvas";

export const salesIndigoInsetCardClass =
  "rounded-lg border border-indigo-200 bg-white dark:border-indigo-800/60 dark:bg-surface-canvas";

export const salesIndigoInsetInputClass =
  "w-full rounded border border-indigo-200 bg-white px-2 py-1 text-xs " +
  "dark:border-indigo-800/60 dark:bg-surface-raised dark:text-surface-ink";

export const salesIndigoOutlineBtnClass =
  "rounded-lg border border-indigo-300 bg-white px-2 py-1 text-xs text-indigo-800 " +
  "hover:bg-indigo-50 disabled:opacity-50 " +
  "dark:border-indigo-700 dark:bg-surface-raised dark:text-indigo-200 dark:hover:bg-indigo-950/50";

/** Admin directory inputs / selects (compact). */
export const adminInputClass =
  "border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink";

export const adminChipInactiveClass =
  "border-gray-200 bg-white hover:bg-gray-50 text-gray-700 " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink-muted dark:hover:bg-surface-hover";

export const adminChipActiveClass =
  "border-indigo-300 bg-indigo-50 text-indigo-800 " +
  "dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200";

export const adminGhostBtnClass =
  "rounded-lg border border-gray-200 bg-white text-sm text-gray-800 hover:bg-gray-50 " +
  "dark:border-surface-border-strong dark:bg-surface-raised dark:text-surface-ink dark:hover:bg-surface-hover";

export const adminDangerGhostBtnClass =
  "rounded-lg border border-red-200 bg-white text-sm text-red-700 hover:bg-red-50 " +
  "dark:border-red-800 dark:bg-surface-raised dark:text-red-300 dark:hover:bg-red-950/40";

export const adminIndigoGhostBtnClass =
  "border border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-100 " +
  "dark:border-indigo-700 dark:bg-surface-raised dark:text-indigo-200 dark:hover:bg-indigo-950/50";

export const adminHelpPopoverClass =
  "text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-2 py-1.5 " +
  "dark:text-surface-ink-muted dark:bg-surface-canvas dark:border-surface-border";

export const adminIconHelpBtnClass =
  "rounded-full border border-gray-300 text-xs text-gray-700 bg-white " +
  "dark:border-surface-border-strong dark:text-surface-ink-muted dark:bg-surface-raised";
